import { useCallback, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory, useParams } from 'react-router-dom'
import { showLoading } from '../common/store/slices/application'
import { useErrorHandle } from '../common/error/errorHandler'
import { executeGetTodayConditionInputDetail } from '../../dataAccess/webApi/dao/reservationsDao'
import { ApiResponse } from '../../dataAccess/webApi/common/apiCaller'
import { FieldPath, useForm } from 'react-hook-form'
import { GetTodayConditionInputDetailDto, PutTodayConditionInputDto } from '../../dataAccess/webApi/dto/reservationsDto'
import { getDecodeMstValue } from '../common/getDecodeMstValue'
import { PostDecodeMstDto } from '../../dataAccess/webApi/dto/decodeMstDto'
import {
  reservationBrestFeedingDetail,
  reservationEatingMethod,
  todayConditionAmountOfFood,
  todayConditionBelongings,
  todayConditionDefecationTiming,
  todayConditionEscortType,
  todayConditionInputNapType,
  yesNo,
} from '../common/constant/classification'
import { getClassificationValueLabels } from '../../i18n'
import {
  clearInput,
  clearReservationInfo,
  clearTodayConditionInputFieldsInfo,
  selectInput,
  setInput,
  setReservationInfo,
  setTodayConditionInputFieldsInfo,
} from '../common/store/slices/facilityReserveTodayConditionInput'
import { facilityReservationTodayConditionInputConfirmationUrl } from '../common/constant/appUrl'
import { useOperationLog } from '../common/operationLog'
import { OperationId } from '../common/constant/operationLog'

interface UrlParams {
  reservationNo: string
}

export interface Inputs {
  /** 体温整数部分（登園前） */
  morningBodyTemperatureIntPart: number | null
  /** 体温小数部分（登園前） */
  morningBodyTemperatureFracPart: number | null
  /** 測定時刻（今朝の体温） */
  morningBodyTemperatureMeasurementTime: string | null
  /** 体温整数部分（朝） */
  afterComingAmBodyTemperatureIntPart: number | null
  /** 体温小数部分（朝） */
  afterComingAmBodyTemperatureFracPart: number | null
  /** 体温整数部分（夕） */
  afterComingPmBodyTemperatureIntPart: number | null
  /** 体温小数部分（夕） */
  afterComingPmBodyTemperatureFracPart: number | null
  /** 咳 */
  cough: string | null
  /** 備考(咳) */
  coughNote: string | null
  /** 鼻水 */
  runnyNose: string | null
  /** 備考(鼻水) */
  runnyNoseNote: string | null
  /** 湿疹 */
  eczema: string | null
  /** 軟膏名 */
  eczemaName: string | null
  /** アレルギー */
  allergy: string | null
  /** 備考(アレルギー) */
  allergyNote: string | null
  /** 食物アレルギー */
  foodAllergy: string | null
  /** 備考(食物アレルギー) */
  foodAllergyNote: string | null
  /** 薬 */
  medicine: string | null
  /** 備考(薬) */
  medicineName: string | null
  /** 機嫌 */
  mood: string | null
  /** 備考(機嫌) */
  moodNote: string | null
  /** 病気 */
  disease: string | null
  /** 1か月以内にかかった病気 */
  diseaseWithinMonth: string | null
  /** 備考(病気) */
  diseaseNote: string | null
  /** 予防接種 */
  vaccine: string | null
  /** 予防接種時期 */
  vaccineMonthsAgo: number | null
  /** 備考(予防接種) */
  vaccineNote: string | null
  /** 病院受診 */
  hospitalVisit: string | null
  /** 病院受診時期 */
  hospitalVisitMonthsAgo: number | null
  /** 備考(病院受診) */
  hospitalVisitNote: string | null
  /** 感染症 */
  infection: string | null
  /** 備考(感染症) */
  infectionNote: string | null
  /** 本日の体調に関する注意点 */
  todayConditionPoint: string | null
  /** 備考(本日の体調) */
  todayConditionPointNote: string | null
  /** 散歩 */
  stroll: string | null
  /** 昨夜の睡眠 */
  lastNightSleep: string | null
  /** 昨夜の就寝時刻 */
  lastNightFromTime: string | null
  /** 今朝の起床時刻 */
  lastNightToTime: string | null
  /** お昼寝 */
  nap: string | null
  /** お昼寝の種類 */
  napType: string | null
  /** 午前睡眠開始時刻 */
  napAmFromTime: string | null
  /** 午前睡眠終了時刻 */
  napAmToTime: string | null
  /** 午後睡眠開始時刻 */
  napPmFromTime: string | null
  /** 午後睡眠終了時刻 */
  napPmToTime: string | null
  /** 排便タイミング */
  defecationTiming: string | null
  /** 排便時刻 */
  defecationHours: number | null
  /** 排便日 */
  defecationDaysAgo: number | null
  /** 排便の状態 */
  defecationCondition: string | null
  /** 食事 */
  meal: string | null
  /** 食事時刻 */
  mealTime: string | null
  /** 栄養方法 */
  eatingMethod: string | null
  /** 朝食 */
  breakfast: string | null
  /** 朝食時刻 */
  breakfastTime: string | null
  /** 朝食食事内容 */
  breakfastWhat: string | null
  /** 昼食 */
  lunch: string | null
  /** 朝食時刻 */
  lunchTime: string | null
  /** 朝食食事内容 */
  lunchWhat: string | null
  /** おやつ */
  snack: string | null
  /** おやつ時刻 */
  snackTime: string | null
  /** 栄養方法詳細 */
  eatingMethodDetail: string | null
  /** ミルク量 */
  milkAmount: number | null
  /** ミルク接種時刻 */
  milkTime: string | null
  /** 母乳接種時刻 */
  breastMilkTime: string | null
  /** 送迎者種類 */
  escortType: string | null
  /** 送迎者1氏名 */
  escort1Name: string | null
  /** 送迎者1続柄 */
  escort1Relationship: string | null
  /** 送迎者1電話番号 */
  escort1Tel: string | null
  /** 送迎者2氏名 */
  escort2Name: string | null
  /** 送迎者2続柄 */
  escort2Relationship: string | null
  /** 送迎者2電話番号 */
  escort2Tel: string | null
  /** 持ち物 */
  belongings: string[] | null
  /** ミルク回数 */
  belongingsMilkCount: number | null
  /** ミルクを溶かすお湯の量 */
  belongingsMilkDissolveHotWater: number | null
  /** 持ち物（その他） */
  belongingsOther: string | null
  /** 備考 */
  note: string | null
}

export type facilityReservationTodayConditionInputKey = FieldPath<Inputs>

export interface TodayConditionInputDecodeMst {
  napType: PostDecodeMstDto[]
  defecationTiming: PostDecodeMstDto[]
  defecationCondition: PostDecodeMstDto[]
  eatingMethod: PostDecodeMstDto[]
  amountOfFood: PostDecodeMstDto[]
  brestFeedingDetail: PostDecodeMstDto[]
  escortType: PostDecodeMstDto[]
  belongings: PostDecodeMstDto[]
}

const TODAY_CONDITION_INPUT_DECODE_MST_LIST = {
  /** お昼寝の種類 */
  napType: 'nap_type',
  // 排便タイミング
  defecationTiming: 'defecation_timing',
  // 便の状態
  defecationCondition: 'defecation_condition',
  // 栄養方法
  eatingMethod: 'eating_method',
  // 食事量
  amountOfFood: 'amount_of_food',
  // 栄養方法詳細
  brestFeedingDetail: 'brest_feeding_detail',
  // 送迎者種類
  escortType: 'escort_type',
  // 持ち物
  belongings: 'belongings',
}

export const useAction = () => {
  const history = useHistory()
  const { reservationNo } = useParams<UrlParams>()

  const errorHandle = useErrorHandle()
  const dispatch = useDispatch()

  const { addOperationLog } = useOperationLog()

  const [todayConditionInputDetail, setTodayConditionInputDetail] = useState<GetTodayConditionInputDetailDto | null>(
    null
  )
  const [todayConditionInputDecodeMst, setTodayConditionInputDecodeMst] = useState<TodayConditionInputDecodeMst | null>(
    null
  )
  const [isCough, setIsCough] = useState<boolean>(false)
  const [isRunnyNose, setIsRunnyNose] = useState<boolean>(false)
  const [isEczema, setIsEczema] = useState<boolean>(false)
  const [isAllergy, setIsAllergy] = useState<boolean>(false)
  const [isFoodAllergy, setIsFoodAllergy] = useState<boolean>(false)
  const [isMedicine, setIsMedicine] = useState<boolean>(false)
  const [isMood, setIsMood] = useState<boolean>(false)
  const [isDisease, setIsDisease] = useState<boolean>(false)
  const [isVaccine, setIsVaccine] = useState<boolean>(false)
  const [isHospitalVisit, setIsHospitalVisit] = useState<boolean>(false)
  const [isInfection, setIsInfection] = useState<boolean>(false)
  const [isTodayConditionPoint, setIsTodayConditionPoint] = useState<boolean>(false)
  const [isStroll, setIsStroll] = useState<boolean>(false)
  const [isLastNightSleep, setIsLastNightSleep] = useState<boolean>(false)
  const [isNap, setIsNap] = useState<boolean>(false)
  const [napType, setNapType] = useState<string | null>(null)
  const [defecationTiming, setDefecationTiming] = useState<string | null>(null)
  const [isMeal, setIsMeal] = useState<boolean>(false)
  const [eatingMethod, setEatingMethod] = useState<string | null>(null)
  const [breakfast, setBreakfast] = useState<string | null>(null)
  const [lunch, setLunch] = useState<string | null>(null)
  const [snack, setSnack] = useState<string | null>(null)
  const [eatingMethodDetail, setEatingMethodDetail] = useState<string | null>(null)
  const [escortType, setEscortType] = useState<string | null>(null)
  const [belongings, setBelongings] = useState<string | null>(null)
  const [isShowNoChange, setIsShowNoChange] = useState<boolean>(false)
  const [initPutTodayConditionInputDto, setInitPutTodayConditionInputDto] = useState<PutTodayConditionInputDto | null>(
    null
  )

  const formMethods = useForm<Inputs>()
  const inputState = useSelector(selectInput)

  // 健康チェック入力では選択肢を「はい、いいえ」のラベルで表示するため言語ファイルからラベル情報を取得
  const yesNos = getClassificationValueLabels(
    'facilityReservationTodayConditionInput.form.common.yesNo',
    Object.values(yesNo)
  ).sort((a, b) => parseInt(b.value) - parseInt(a.value));

  // 初期化処理
  const initialize = useCallback(async () => {
    // 当日入力に必要な情報を取得する
    const todayConditionInputDetailRes: ApiResponse<GetTodayConditionInputDetailDto> =
      await executeGetTodayConditionInputDetail(reservationNo)
    const todayConditionInputDetail = todayConditionInputDetailRes.result

    if (todayConditionInputDetail) {
      const editInitPutTodayConditionInputDto = _responseDtoToInputData(todayConditionInputDetail)
      setInitPutTodayConditionInputDto(editInitPutTodayConditionInputDto)
      //確認画面から当画面に戻ってきた場合、reduxで保持している入力値をformに反映する
      if (inputState) {
        _inputStateToInputData(inputState)
      }
      setTodayConditionInputDetail(todayConditionInputDetail)
    }

    // 入力時の選択肢として必要な情報をdecode_mstから取得する
    const getDecodeMstValues: TodayConditionInputDecodeMst = await getDecodeMstValue(
      TODAY_CONDITION_INPUT_DECODE_MST_LIST
    )
    if (getDecodeMstValues) {
      setTodayConditionInputDecodeMst(getDecodeMstValues)
    }

    //確認画面から当画面に戻ってくる時のために入力値をreduxで保持している
    //reduxに入力値を保持し続けると他の予約に対する当日入力時にも入力値を引き継いでしまうためクリアする
    dispatch(clearReservationInfo())
    dispatch(clearTodayConditionInputFieldsInfo())
    dispatch(clearInput())
  }, [reservationNo, inputState])

  // 初回レンダリング時の処理
  useEffect(() => {
    addOperationLog({ operationId: OperationId.OP_00000193 })

    dispatch(showLoading(errorHandle(() => initialize())))
    // eslint-disable-next-line react-hooks/exhaustive-deps

    // 入力フォームの値が更新されたことを検知するためのサブスクリプションを定義
    const subscription = formMethods.watch((values, { name }) => {
      if (name === 'cough') {
        setIsCough(values.cough === '1')
      }
      if (name === 'runnyNose') {
        setIsRunnyNose(values.runnyNose === '1')
      }
      if (name === 'eczema') {
        setIsEczema(values.eczema === '1')
        // 必須チェックの時にnullだと都合が悪いので空文字を設定する
        if (!formMethods.getValues('eczemaName')) {
          formMethods.setValue('eczemaName', '')
        }
      }
      if (name === 'allergy') {
        setIsAllergy(values.allergy === '1')
      }
      if (name === 'foodAllergy') {
        setIsFoodAllergy(values.foodAllergy === '1')
      }
      if (name === 'medicine') {
        setIsMedicine(values.medicine === '1')
        // 必須チェックの時にnullだと都合が悪いので空文字を設定する
        if (!formMethods.getValues('medicineName')) {
          formMethods.setValue('medicineName', '')
        }
      }
      if (name === 'mood') {
        setIsMood(values.mood === '1')
      }
      if (name === 'disease') {
        setIsDisease(values.disease === '1')
      }
      if (name === 'vaccine') {
        setIsVaccine(values.vaccine === '1')
      }
      if (name === 'hospitalVisit') {
        setIsHospitalVisit(values.hospitalVisit === '1')
      }
      if (name === 'infection') {
        setIsInfection(values.infection === '1')
      }
      if (name === 'todayConditionPoint') {
        setIsTodayConditionPoint(values.todayConditionPoint === '1')
      }
      if (name === 'stroll') {
        setIsStroll(values.stroll === '1')
      }
      if (name === 'lastNightSleep') {
        setIsLastNightSleep(values.lastNightSleep === '1')
      }
      if (name === 'nap') {
        setIsNap(values.nap === '1')
      }
      if (name === 'napType') {
        setNapType(values.napType ? values.napType : null)
      }
      if (name === 'defecationTiming') {
        setDefecationTiming(values.defecationTiming ? values.defecationTiming : null)
      }
      if (name === 'meal') {
        setIsMeal(values.meal === '1')
      }
      if (name === 'eatingMethod') {
        setEatingMethod(values.eatingMethod ? values.eatingMethod : null)
      }
      if (name === 'breakfast') {
        setBreakfast(values.breakfast ? values.breakfast : null)
        // 必須チェックの時にnullだと都合が悪いので空文字を設定する
        if (!formMethods.getValues('breakfastWhat')) {
          formMethods.setValue('breakfastWhat', '')
        }
      }
      if (name === 'lunch') {
        setLunch(values.lunch ? values.lunch : null)
        // 必須チェックの時にnullだと都合が悪いので空文字を設定する
        if (!formMethods.getValues('lunchWhat')) {
          formMethods.setValue('lunchWhat', '')
        }
      }
      if (name === 'snack') {
        setSnack(values.snack ? values.snack : null)
      }
      if (name === 'eatingMethodDetail') {
        setEatingMethodDetail(values.eatingMethodDetail ? values.eatingMethodDetail : null)
      }
      if (name === 'escortType') {
        setEscortType(values.escortType ? values.escortType : null)
      }
      if (name === 'belongings') {
        setBelongings(values.belongings ? values.belongings.join(',') : '')
        if (
          values.belongings &&
          values.belongings.includes(todayConditionBelongings.other) &&
          !formMethods.getValues('belongingsOther')
        ) {
          formMethods.setValue('belongingsOther', '')
        }
      }
    })
    return () => subscription.unsubscribe()
  }, [])

  // フォームのボタン押下
  const onSubmit = useCallback(
    (data: Inputs) => {
      const putTodayConditionInputDto = _inputDataToRequstDto(todayConditionInputDetail, data)
      // 初期表示から値の変更があるかチェックする
      // Inputsをそのまま比較すると、一度設定したが親要素の選択値によって非表示になった項目の値がそのまま残っていて上手く比較できないため
      // requestBodyに変換した値を比較する
      if (todayConditionInputDetail && putTodayConditionInputDto && _inputDataDiffCheck(putTodayConditionInputDto)) {
        // 差分がある場合は確認画面に遷移する
        dispatch(setReservationInfo(todayConditionInputDetail.reservationInfo))
        dispatch(setTodayConditionInputFieldsInfo(todayConditionInputDetail.todayConditionInputFieldsInfo))
        dispatch(setInput(putTodayConditionInputDto))
        history.push(facilityReservationTodayConditionInputConfirmationUrl.url(reservationNo))
      } else {
        // 差分がない場合は差分がない旨を通知するダイアログを開く
        setIsShowNoChange(true)
        return
      }
    },
    [
      formMethods,
      todayConditionInputDetail,
      initPutTodayConditionInputDto,
      isCough,
      isRunnyNose,
      isEczema,
      isAllergy,
      isFoodAllergy,
      isMedicine,
      isMood,
      isDisease,
      isVaccine,
      isHospitalVisit,
      isInfection,
      isTodayConditionPoint,
      isStroll,
      isLastNightSleep,
      isNap,
      napType,
      defecationTiming,
      isMeal,
      eatingMethod,
      breakfast,
      lunch,
      snack,
      eatingMethodDetail,
      escortType,
      belongings,
    ]
  )

  // 入力フォームの内容をリクエストボディに変換する
  const _inputDataToRequstDto = (
    todayConditionInputDetailData: GetTodayConditionInputDetailDto | null,
    data: Inputs | null
  ): PutTodayConditionInputDto | null => {
    if (!data) {
      return null
    }

    const requestBody: PutTodayConditionInputDto = {
      // =====きょうの様子セクション===============
      // 体温（登園前）
      morningBodyTemperature:
        data.morningBodyTemperatureIntPart
          ? Number(`${data.morningBodyTemperatureIntPart}.${data.morningBodyTemperatureFracPart == null || isNaN(data.morningBodyTemperatureFracPart) ? 0 : data.morningBodyTemperatureFracPart}`)
          : null,
      // 測定時刻（登園前）
      morningBodyTemperatureMeasurementTime: data.morningBodyTemperatureMeasurementTime
        ? String(data.morningBodyTemperatureMeasurementTime)
        : null,
      // 体温（午前）
      afterComingAmBodyTemperature:
        data.afterComingAmBodyTemperatureIntPart
          ? Number(`${data.afterComingAmBodyTemperatureIntPart}.${data.afterComingAmBodyTemperatureFracPart == null || isNaN(data.afterComingAmBodyTemperatureFracPart) ? 0 : data.afterComingAmBodyTemperatureFracPart}`)
          : null,
      // 体温（午後）
      afterComingPmBodyTemperature:
        data.afterComingPmBodyTemperatureIntPart
          ? Number(`${data.afterComingPmBodyTemperatureIntPart}.${data.afterComingPmBodyTemperatureFracPart == null || isNaN(data.afterComingPmBodyTemperatureFracPart) ? 0 : data.afterComingPmBodyTemperatureFracPart}`)
          : null,
      // 咳
      cough: data.cough ? data.cough : null,
      // 備考(咳)
      coughNote: data.cough === '1' ? data.coughNote : null,
      // 鼻水
      runnyNose: data.runnyNose ? data.runnyNose : null,
      // 備考(鼻水)
      runnyNoseNote: data.runnyNose === '1' ? data.runnyNoseNote : null,
      // 湿疹
      eczema: data.eczema ? data.eczema : null,
      // 軟膏名
      eczemaName: data.eczema === '1' ? data.eczemaName : null,
      // アレルギー
      allergy: data.allergy ? data.allergy : null,
      // 備考(アレルギー)
      allergyNote: data.allergy === '1' ? data.allergyNote : null,
      // 食物アレルギー
      foodAllergy: data.foodAllergy ? data.foodAllergy : null,
      // 備考(食物アレルギー)
      foodAllergyNote: data.foodAllergy === '1' ? data.foodAllergyNote : null,
      // 薬
      medicine: data.medicine ? data.medicine : null,
      // 備考(薬)
      medicineName: data.medicine === '1' ? data.medicineName : null,
      // 機嫌
      mood: data.mood ? data.mood : null,
      // 備考(機嫌)
      moodNote: data.mood === '1' ? data.moodNote : null,
      // 病気
      disease: data.disease ? data.disease : null,
      // 1か月以内にかかった病気
      diseaseWithinMonth: data.disease === '1' ? (data.diseaseWithinMonth === '1' ? '1' : '0') : null,
      // 備考(病気)
      diseaseNote: data.disease === '1' ? data.diseaseNote : null,
      // 予防接種
      vaccine: data.vaccine ? data.vaccine : null,
      // 予防接種時期
      vaccineMonthsAgo: data.vaccine === '1' ? data.vaccineMonthsAgo : null,
      // 備考(予防接種)
      vaccineNote: data.vaccine === '1' ? data.vaccineNote : null,
      // 病院受診
      hospitalVisit: data.hospitalVisit ? data.hospitalVisit : null,
      // 病院受診時期
      hospitalVisitMonthsAgo: data.hospitalVisit === '1' ? data.hospitalVisitMonthsAgo : null,
      // 備考(病院受診)
      hospitalVisitNote: data.hospitalVisit === '1' ? data.hospitalVisitNote : null,
      // 感染症
      infection: data.infection ? data.infection : null,
      // 備考(感染症)
      infectionNote: data.infection === '1' ? data.infectionNote : null,
      // 本日の体調に関する注意点
      todayConditionPoint: data.todayConditionPoint ? data.todayConditionPoint : null,
      // 備考(本日の体調)
      todayConditionPointNote: data.todayConditionPoint === '1' ? data.todayConditionPointNote : null,
      // 散歩
      stroll: data.stroll ? data.stroll : null,

      // =====睡眠セクション===============
      // 昨夜の睡眠
      lastNightSleep: data.lastNightSleep ? data.lastNightSleep : null,
      // 昨夜の就寝時刻
      lastNightFromTime: data.lastNightFromTime ? data.lastNightFromTime : null,
      // 今朝の起床時刻
      lastNightToTime: data.lastNightToTime ? data.lastNightToTime : null,
      // お昼寝
      nap: data.nap ? data.nap : null,
      // お昼寝の種類
      napType: isNap ? data.napType : null,
      // 午前睡眠開始時刻
      napAmFromTime:
        data.nap === '1' &&
          (data.napType === todayConditionInputNapType.amOnly || data.napType === todayConditionInputNapType.both)
          ? data.napAmFromTime
          : null,
      // 午前睡眠終了時刻
      napAmToTime:
        data.nap === '1' &&
          (data.napType === todayConditionInputNapType.amOnly || data.napType === todayConditionInputNapType.both)
          ? data.napAmToTime
          : null,
      // 午後睡眠開始時刻
      napPmFromTime:
        data.nap === '1' &&
          (data.napType === todayConditionInputNapType.pmOnly || data.napType === todayConditionInputNapType.both)
          ? data.napPmFromTime
          : null,
      // 午後睡眠終了時刻
      napPmToTime:
        data.nap === '1' &&
          (data.napType === todayConditionInputNapType.pmOnly || data.napType === todayConditionInputNapType.both)
          ? data.napPmToTime
          : null,

      // =====排便セクション===============
      // 排便タイミング
      defecationTiming: data.defecationTiming ? data.defecationTiming : null,
      // 排便時刻
      defecationHours: data.defecationTiming === todayConditionDefecationTiming.yesterday ? data.defecationHours : null,
      // 排便日
      defecationDaysAgo:
        data.defecationTiming === todayConditionDefecationTiming.twoDaysAgoOrLater ? data.defecationDaysAgo : null,
      // 排便の状態
      defecationCondition: data.defecationCondition ? data.defecationCondition : null,

      // =====食事セクション===============
      // 食事
      meal: data.meal ? data.meal : null,
      // 食事時刻
      mealTime: isMeal ? data.mealTime : null,
      // 栄養方法
      eatingMethod: data.eatingMethod ? data.eatingMethod : null,
      // 朝食
      breakfast:
        data.eatingMethod && data.eatingMethod !== reservationEatingMethod.brestFeeding ? data.breakfast : null,
      // 朝食時刻
      breakfastTime:
        data.eatingMethod &&
          data.eatingMethod !== reservationEatingMethod.brestFeeding &&
          data.breakfast &&
          data.breakfast !== todayConditionAmountOfFood.none
          ? data.breakfastTime
          : null,
      // 朝食食事内容
      breakfastWhat:
        data.eatingMethod &&
          data.eatingMethod !== reservationEatingMethod.brestFeeding &&
          data.breakfast &&
          data.breakfast !== todayConditionAmountOfFood.none
          ? data.breakfastWhat
          : null,
      // 昼食
      lunch: data.eatingMethod && data.eatingMethod !== reservationEatingMethod.brestFeeding ? data.lunch : null,
      // 昼食時刻
      lunchTime:
        data.eatingMethod &&
          data.eatingMethod !== reservationEatingMethod.brestFeeding &&
          data.lunch &&
          data.lunch !== todayConditionAmountOfFood.none
          ? data.lunchTime
          : null,
      // 昼食食事内容
      lunchWhat:
        data.eatingMethod &&
          data.eatingMethod !== reservationEatingMethod.brestFeeding &&
          data.lunch &&
          data.lunch !== todayConditionAmountOfFood.none
          ? data.lunchWhat
          : null,
      // おやつ
      snack: data.eatingMethod && data.eatingMethod !== reservationEatingMethod.brestFeeding ? data.snack : null,
      // おやつ時刻
      snackTime:
        data.eatingMethod &&
          data.eatingMethod !== reservationEatingMethod.brestFeeding &&
          data.snack &&
          data.snack === '1'
          ? data.snackTime
          : null,
      // 栄養方法詳細
      eatingMethodDetail: data.eatingMethod === reservationEatingMethod.brestFeeding ? data.eatingMethodDetail : null,
      // ミルク量
      milkAmount:
        data.eatingMethod === reservationEatingMethod.brestFeeding &&
          data.eatingMethodDetail &&
          data.eatingMethodDetail !== reservationBrestFeedingDetail.breastMilk
          ? data.milkAmount
          : null,
      // ミルク接種時刻
      milkTime:
        data.eatingMethod === reservationEatingMethod.brestFeeding &&
          data.eatingMethodDetail &&
          data.eatingMethodDetail !== reservationBrestFeedingDetail.breastMilk
          ? data.milkTime
          : null,
      // 母乳接種時刻
      breastMilkTime:
        data.eatingMethod === reservationEatingMethod.brestFeeding &&
          data.eatingMethodDetail &&
          data.eatingMethodDetail !== reservationBrestFeedingDetail.formula
          ? data.breastMilkTime
          : null,

      // =====その他セクション===============
      // 送迎者種類
      escortType: data.escortType ? data.escortType : null,
      // 送迎者1氏名
      escort1Name:
        data.escortType && data.escortType === todayConditionEscortType.transportationByAgent ? data.escort1Name : null,
      // 送迎者1続柄
      escort1Relationship:
        data.escortType && data.escortType === todayConditionEscortType.transportationByAgent
          ? data.escort1Relationship
          : null,
      // 送迎者1電話番号
      escort1Tel:
        data.escortType && data.escortType === todayConditionEscortType.transportationByAgent ? data.escort1Tel : null,
      // 送迎者2氏名
      escort2Name:
        data.escortType && data.escortType === todayConditionEscortType.transportationByAgent ? data.escort2Name : null,
      // 送迎者2続柄
      escort2Relationship:
        data.escortType && data.escortType === todayConditionEscortType.transportationByAgent
          ? data.escort2Relationship
          : null,
      // 送迎者2電話番号
      escort2Tel:
        data.escortType && data.escortType === todayConditionEscortType.transportationByAgent ? data.escort2Tel : null,
      // 持ち物
      belongings: data.belongings && data.belongings.length > 0 ? data.belongings.join(',') : null,
      // ミルク回数
      belongingsMilkCount:
        data.belongings && data.belongings.includes(todayConditionBelongings.milk) ? data.belongingsMilkCount : null,
      // ミルクを溶かすお湯の量
      belongingsMilkDissolveHotWater:
        data.belongings && data.belongings.includes(todayConditionBelongings.milk)
          ? data.belongingsMilkDissolveHotWater
          : null,
      // 持ち物（その他）
      belongingsOther:
        data.belongings && data.belongings.includes(todayConditionBelongings.other) ? data.belongingsOther : null,
      // 備考
      note: data.note,

      // =====排他制御用===============
      // 更新日時
      updateDateTime: todayConditionInputDetailData
        ? todayConditionInputDetailData.todayConditionInputInfo.updateDateTime
        : null,
    }

    return requestBody
  }

  // レスポンスボディの内容を入力フォームに変換（設定）する
  const _responseDtoToInputData = (
    getTodayConditionInputDetailDto: GetTodayConditionInputDetailDto
  ): PutTodayConditionInputDto | null => {
    const { todayConditionInputInfo } = getTodayConditionInputDetailDto

    // フォームの初期値を設定する
    //体温（登園前）
    let morningBodyTemperatureIntPart = null
    let morningBodyTemperatureFracPart = null
    if (todayConditionInputInfo.morningBodyTemperature) {
      const [intPart, FracPart] = String(todayConditionInputInfo.morningBodyTemperature).split('.')
      morningBodyTemperatureIntPart = Number(intPart)
      morningBodyTemperatureFracPart = FracPart ? Number(FracPart) : 0
    }
    formMethods.setValue('morningBodyTemperatureIntPart', morningBodyTemperatureIntPart)
    formMethods.setValue('morningBodyTemperatureFracPart', morningBodyTemperatureFracPart)
    formMethods.setValue(
      'morningBodyTemperatureMeasurementTime',
      todayConditionInputInfo.morningBodyTemperatureMeasurementTime
    )
    //体温（午前）
    let afterComingAmBodyTemperatureIntPart = null
    let afterComingAmBodyTemperatureFracPart = null
    if (todayConditionInputInfo.afterComingAmBodyTemperature) {
      const [intPart, FracPart] = String(todayConditionInputInfo.afterComingAmBodyTemperature).split('.')
      afterComingAmBodyTemperatureIntPart = Number(intPart)
      afterComingAmBodyTemperatureFracPart = FracPart ? Number(FracPart) : 0
    }
    formMethods.setValue('afterComingAmBodyTemperatureIntPart', afterComingAmBodyTemperatureIntPart)
    formMethods.setValue('afterComingAmBodyTemperatureFracPart', afterComingAmBodyTemperatureFracPart)
    //体温（午後）
    let afterComingPmBodyTemperatureIntPart = null
    let afterComingPmBodyTemperatureFracPart = null
    if (todayConditionInputInfo.afterComingPmBodyTemperature) {
      const [intPart, FracPart] = String(todayConditionInputInfo.afterComingPmBodyTemperature).split('.')
      afterComingPmBodyTemperatureIntPart = Number(intPart)
      afterComingPmBodyTemperatureFracPart = FracPart ? Number(FracPart) : 0
    }
    formMethods.setValue('afterComingPmBodyTemperatureIntPart', afterComingPmBodyTemperatureIntPart)
    formMethods.setValue('afterComingPmBodyTemperatureFracPart', afterComingPmBodyTemperatureFracPart)
    formMethods.setValue('cough', todayConditionInputInfo.cough)
    formMethods.setValue('coughNote', todayConditionInputInfo.coughNote)
    formMethods.setValue('runnyNose', todayConditionInputInfo.runnyNose)
    formMethods.setValue('runnyNoseNote', todayConditionInputInfo.runnyNoseNote)
    formMethods.setValue('eczema', todayConditionInputInfo.eczema)
    formMethods.setValue('eczemaName', todayConditionInputInfo.eczemaName)
    formMethods.setValue('allergy', todayConditionInputInfo.allergy)
    formMethods.setValue('allergyNote', todayConditionInputInfo.allergyNote)
    formMethods.setValue('foodAllergy', todayConditionInputInfo.foodAllergy)
    formMethods.setValue('foodAllergyNote', todayConditionInputInfo.foodAllergyNote)
    formMethods.setValue('medicine', todayConditionInputInfo.medicine)
    formMethods.setValue('medicineName', todayConditionInputInfo.medicineName)
    formMethods.setValue('mood', todayConditionInputInfo.mood)
    formMethods.setValue('moodNote', todayConditionInputInfo.moodNote)
    formMethods.setValue('disease', todayConditionInputInfo.disease)
    formMethods.setValue('diseaseWithinMonth', todayConditionInputInfo.diseaseWithinMonth)
    formMethods.setValue('diseaseNote', todayConditionInputInfo.diseaseNote)
    formMethods.setValue('vaccine', todayConditionInputInfo.vaccine)
    formMethods.setValue('vaccineMonthsAgo', todayConditionInputInfo.vaccineMonthsAgo)
    formMethods.setValue('vaccineNote', todayConditionInputInfo.vaccineNote)
    formMethods.setValue('hospitalVisit', todayConditionInputInfo.hospitalVisit)
    formMethods.setValue('hospitalVisitMonthsAgo', todayConditionInputInfo.hospitalVisitMonthsAgo)
    formMethods.setValue('hospitalVisitNote', todayConditionInputInfo.hospitalVisitNote)
    formMethods.setValue('infection', todayConditionInputInfo.infection)
    formMethods.setValue('infectionNote', todayConditionInputInfo.infectionNote)
    formMethods.setValue('todayConditionPoint', todayConditionInputInfo.todayConditionPoint)
    formMethods.setValue('todayConditionPointNote', todayConditionInputInfo.todayConditionPointNote)
    formMethods.setValue('stroll', todayConditionInputInfo.stroll)
    formMethods.setValue('lastNightSleep', todayConditionInputInfo.lastNightSleep)
    formMethods.setValue('lastNightFromTime', todayConditionInputInfo.lastNightFromTime)
    formMethods.setValue('lastNightToTime', todayConditionInputInfo.lastNightToTime)
    formMethods.setValue('nap', todayConditionInputInfo.nap)
    formMethods.setValue('napType', todayConditionInputInfo.napType)
    formMethods.setValue('napAmFromTime', todayConditionInputInfo.napAmFromTime)
    formMethods.setValue('napAmToTime', todayConditionInputInfo.napAmToTime)
    formMethods.setValue('napPmFromTime', todayConditionInputInfo.napPmFromTime)
    formMethods.setValue('napPmToTime', todayConditionInputInfo.napPmToTime)
    formMethods.setValue('defecationTiming', todayConditionInputInfo.defecationTiming)
    formMethods.setValue('defecationHours', todayConditionInputInfo.defecationHours)
    formMethods.setValue('defecationDaysAgo', todayConditionInputInfo.defecationDaysAgo)
    formMethods.setValue('defecationCondition', todayConditionInputInfo.defecationCondition)
    formMethods.setValue('meal', todayConditionInputInfo.meal)
    formMethods.setValue('mealTime', todayConditionInputInfo.mealTime)
    formMethods.setValue('eatingMethod', todayConditionInputInfo.eatingMethod)
    formMethods.setValue('breakfast', todayConditionInputInfo.breakfast)
    formMethods.setValue('breakfastTime', todayConditionInputInfo.breakfastTime)
    formMethods.setValue('breakfastWhat', todayConditionInputInfo.breakfastWhat)
    formMethods.setValue('lunch', todayConditionInputInfo.lunch)
    formMethods.setValue('lunchTime', todayConditionInputInfo.lunchTime)
    formMethods.setValue('lunchWhat', todayConditionInputInfo.lunchWhat)
    formMethods.setValue('snack', todayConditionInputInfo.snack)
    formMethods.setValue('snackTime', todayConditionInputInfo.snackTime)
    formMethods.setValue('eatingMethodDetail', todayConditionInputInfo.eatingMethodDetail)
    formMethods.setValue('milkAmount', todayConditionInputInfo.milkAmount)
    formMethods.setValue('milkTime', todayConditionInputInfo.milkTime)
    formMethods.setValue('breastMilkTime', todayConditionInputInfo.breastMilkTime)
    formMethods.setValue('escortType', todayConditionInputInfo.escortType)
    formMethods.setValue('escort1Name', todayConditionInputInfo.escort1Name)
    formMethods.setValue('escort1Relationship', todayConditionInputInfo.escort1Relationship)
    formMethods.setValue('escort1Tel', todayConditionInputInfo.escort1Tel ? todayConditionInputInfo.escort1Tel : '')
    formMethods.setValue('escort2Name', todayConditionInputInfo.escort2Name)
    formMethods.setValue('escort2Relationship', todayConditionInputInfo.escort2Relationship)
    formMethods.setValue('escort2Tel', todayConditionInputInfo.escort2Tel ? todayConditionInputInfo.escort2Tel : '')
    formMethods.setValue(
      'belongings',
      todayConditionInputInfo.belongings ? todayConditionInputInfo.belongings.split(',') : []
    )
    formMethods.setValue('belongingsMilkCount', todayConditionInputInfo.belongingsMilkCount)
    formMethods.setValue('belongingsMilkDissolveHotWater', todayConditionInputInfo.belongingsMilkDissolveHotWater)
    formMethods.setValue('belongingsOther', todayConditionInputInfo.belongingsOther)
    formMethods.setValue('note', todayConditionInputInfo.note)

    const editInitPutTodayConditionInputDto = _inputDataToRequstDto(todayConditionInputDetail, {
      morningBodyTemperatureIntPart: morningBodyTemperatureIntPart,
      morningBodyTemperatureFracPart: morningBodyTemperatureFracPart,
      morningBodyTemperatureMeasurementTime: todayConditionInputInfo.morningBodyTemperatureMeasurementTime,
      afterComingAmBodyTemperatureIntPart: afterComingAmBodyTemperatureIntPart,
      afterComingAmBodyTemperatureFracPart: afterComingAmBodyTemperatureFracPart,
      afterComingPmBodyTemperatureIntPart: afterComingPmBodyTemperatureIntPart,
      afterComingPmBodyTemperatureFracPart: afterComingPmBodyTemperatureFracPart,
      cough: todayConditionInputInfo.cough,
      coughNote: todayConditionInputInfo.coughNote,
      runnyNose: todayConditionInputInfo.runnyNose,
      runnyNoseNote: todayConditionInputInfo.runnyNoseNote,
      eczema: todayConditionInputInfo.eczema,
      eczemaName: todayConditionInputInfo.eczemaName,
      allergy: todayConditionInputInfo.allergy,
      allergyNote: todayConditionInputInfo.allergyNote,
      foodAllergy: todayConditionInputInfo.foodAllergy,
      foodAllergyNote: todayConditionInputInfo.foodAllergyNote,
      medicine: todayConditionInputInfo.medicine,
      medicineName: todayConditionInputInfo.medicineName,
      mood: todayConditionInputInfo.mood,
      moodNote: todayConditionInputInfo.moodNote,
      disease: todayConditionInputInfo.disease,
      diseaseWithinMonth: todayConditionInputInfo.diseaseWithinMonth,
      diseaseNote: todayConditionInputInfo.diseaseNote,
      vaccine: todayConditionInputInfo.vaccine,
      vaccineMonthsAgo: todayConditionInputInfo.vaccineMonthsAgo,
      vaccineNote: todayConditionInputInfo.vaccineNote,
      hospitalVisit: todayConditionInputInfo.hospitalVisit,
      hospitalVisitMonthsAgo: todayConditionInputInfo.hospitalVisitMonthsAgo,
      hospitalVisitNote: todayConditionInputInfo.hospitalVisitNote,
      infection: todayConditionInputInfo.infection,
      infectionNote: todayConditionInputInfo.infectionNote,
      todayConditionPoint: todayConditionInputInfo.todayConditionPoint,
      todayConditionPointNote: todayConditionInputInfo.todayConditionPointNote,
      stroll: todayConditionInputInfo.stroll,
      lastNightSleep: todayConditionInputInfo.lastNightSleep,
      lastNightFromTime: todayConditionInputInfo.lastNightFromTime,
      lastNightToTime: todayConditionInputInfo.lastNightToTime,
      nap: todayConditionInputInfo.nap,
      napType: todayConditionInputInfo.napType,
      napAmFromTime: todayConditionInputInfo.napAmFromTime,
      napAmToTime: todayConditionInputInfo.napAmToTime,
      napPmFromTime: todayConditionInputInfo.napPmFromTime,
      napPmToTime: todayConditionInputInfo.napPmToTime,
      defecationTiming: todayConditionInputInfo.defecationTiming,
      defecationHours: todayConditionInputInfo.defecationHours,
      defecationDaysAgo: todayConditionInputInfo.defecationDaysAgo,
      defecationCondition: todayConditionInputInfo.defecationCondition,
      meal: todayConditionInputInfo.meal,
      mealTime: todayConditionInputInfo.mealTime,
      eatingMethod: todayConditionInputInfo.eatingMethod,
      breakfast: todayConditionInputInfo.breakfast,
      breakfastTime: todayConditionInputInfo.breakfastTime,
      breakfastWhat: todayConditionInputInfo.breakfastWhat,
      lunch: todayConditionInputInfo.lunch,
      lunchTime: todayConditionInputInfo.lunchTime,
      lunchWhat: todayConditionInputInfo.lunchWhat,
      snack: todayConditionInputInfo.snack,
      snackTime: todayConditionInputInfo.snackTime,
      eatingMethodDetail: todayConditionInputInfo.eatingMethodDetail,
      milkAmount: todayConditionInputInfo.milkAmount,
      milkTime: todayConditionInputInfo.milkTime,
      breastMilkTime: todayConditionInputInfo.breastMilkTime,
      escortType: todayConditionInputInfo.escortType,
      escort1Name: todayConditionInputInfo.escort1Name,
      escort1Relationship: todayConditionInputInfo.escort1Relationship,
      escort1Tel: todayConditionInputInfo.escort1Tel,
      escort2Name: todayConditionInputInfo.escort2Name,
      escort2Relationship: todayConditionInputInfo.escort2Relationship,
      escort2Tel: todayConditionInputInfo.escort2Tel,
      belongings: todayConditionInputInfo.belongings ? todayConditionInputInfo.belongings.split(',') : [],
      belongingsMilkCount: todayConditionInputInfo.belongingsMilkCount,
      belongingsMilkDissolveHotWater: todayConditionInputInfo.belongingsMilkDissolveHotWater,
      belongingsOther: todayConditionInputInfo.belongingsOther,
      note: todayConditionInputInfo.note,
    })
    return editInitPutTodayConditionInputDto
  }

  // 入力フォームの内容と初期表示の内容に差があるかを判定する
  const _inputDataDiffCheck = (data: PutTodayConditionInputDto | null): boolean => {
    if (!data || !initPutTodayConditionInputDto) {
      return false
    }

    // Inputs の全てのキーを取得して比較
    const keys = Object.keys(data) as (keyof PutTodayConditionInputDto)[]

    // フィールドごとに値を比較して、異なる場合は true を返す
    for (const key of keys) {
      if (key === 'updateDateTime') {
        return false
      }
      if (JSON.stringify(data[key]) !== JSON.stringify(initPutTodayConditionInputDto[key])) {
        return true
      }
    }

    // 全てのフィールドが一致する場合は false を返す
    return false
  }

  // recoil管理のstateの内容を入力フォームに変換（設定）する
  const _inputStateToInputData = (inputState: PutTodayConditionInputDto) => {
    // フォームの初期値を設定する
    //体温（登園前）
    let morningBodyTemperatureIntPart = null
    let morningBodyTemperatureFracPart = null
    if (inputState.morningBodyTemperature) {
      const [intPart, FracPart] = String(inputState.morningBodyTemperature).split('.')
      morningBodyTemperatureIntPart = Number(intPart)
      morningBodyTemperatureFracPart = Number(FracPart)
    }
    formMethods.setValue('morningBodyTemperatureIntPart', morningBodyTemperatureIntPart)
    formMethods.setValue('morningBodyTemperatureFracPart', morningBodyTemperatureFracPart)
    formMethods.setValue('morningBodyTemperatureMeasurementTime', inputState.morningBodyTemperatureMeasurementTime)
    //体温（朝）
    let afterComingAmBodyTemperatureIntPart = null
    let afterComingAmBodyTemperatureFracPart = null
    if (inputState.afterComingAmBodyTemperature) {
      const [intPart, FracPart] = String(inputState.afterComingAmBodyTemperature).split('.')
      afterComingAmBodyTemperatureIntPart = Number(intPart)
      afterComingAmBodyTemperatureFracPart = Number(FracPart)
    }
    formMethods.setValue('afterComingAmBodyTemperatureIntPart', afterComingAmBodyTemperatureIntPart)
    formMethods.setValue('afterComingAmBodyTemperatureFracPart', afterComingAmBodyTemperatureFracPart)
    //体温（夕）
    let afterComingPmBodyTemperatureIntPart = null
    let afterComingPmBodyTemperatureFracPart = null
    if (inputState.afterComingPmBodyTemperature) {
      const [intPart, FracPart] = String(inputState.afterComingPmBodyTemperature).split('.')
      afterComingPmBodyTemperatureIntPart = Number(intPart)
      afterComingPmBodyTemperatureFracPart = Number(FracPart)
    }
    formMethods.setValue('afterComingPmBodyTemperatureIntPart', afterComingPmBodyTemperatureIntPart)
    formMethods.setValue('afterComingPmBodyTemperatureFracPart', afterComingPmBodyTemperatureFracPart)
    formMethods.setValue('cough', inputState.cough)
    formMethods.setValue('coughNote', inputState.coughNote)
    formMethods.setValue('runnyNose', inputState.runnyNose)
    formMethods.setValue('runnyNoseNote', inputState.runnyNoseNote)
    formMethods.setValue('eczema', inputState.eczema)
    formMethods.setValue('eczemaName', inputState.eczemaName)
    formMethods.setValue('allergy', inputState.allergy)
    formMethods.setValue('allergyNote', inputState.allergyNote)
    formMethods.setValue('foodAllergy', inputState.foodAllergy)
    formMethods.setValue('foodAllergyNote', inputState.foodAllergyNote)
    formMethods.setValue('medicine', inputState.medicine)
    formMethods.setValue('medicineName', inputState.medicineName)
    formMethods.setValue('mood', inputState.mood)
    formMethods.setValue('moodNote', inputState.moodNote)
    formMethods.setValue('disease', inputState.disease)
    formMethods.setValue('diseaseWithinMonth', inputState.diseaseWithinMonth)
    formMethods.setValue('diseaseNote', inputState.diseaseNote)
    formMethods.setValue('vaccine', inputState.vaccine)
    formMethods.setValue('vaccineMonthsAgo', inputState.vaccineMonthsAgo)
    formMethods.setValue('vaccineNote', inputState.vaccineNote)
    formMethods.setValue('hospitalVisit', inputState.hospitalVisit)
    formMethods.setValue('hospitalVisitMonthsAgo', inputState.hospitalVisitMonthsAgo)
    formMethods.setValue('hospitalVisitNote', inputState.hospitalVisitNote)
    formMethods.setValue('infection', inputState.infection)
    formMethods.setValue('infectionNote', inputState.infectionNote)
    formMethods.setValue('todayConditionPoint', inputState.todayConditionPoint)
    formMethods.setValue('todayConditionPointNote', inputState.todayConditionPointNote)
    formMethods.setValue('stroll', inputState.stroll)
    formMethods.setValue('lastNightSleep', inputState.lastNightSleep)
    formMethods.setValue('lastNightFromTime', inputState.lastNightFromTime)
    formMethods.setValue('lastNightToTime', inputState.lastNightToTime)
    formMethods.setValue('nap', inputState.nap)
    formMethods.setValue('napType', inputState.napType)
    formMethods.setValue('napAmFromTime', inputState.napAmFromTime)
    formMethods.setValue('napAmToTime', inputState.napAmToTime)
    formMethods.setValue('napPmFromTime', inputState.napPmFromTime)
    formMethods.setValue('napPmToTime', inputState.napPmToTime)
    formMethods.setValue('defecationTiming', inputState.defecationTiming)
    formMethods.setValue('defecationHours', inputState.defecationHours)
    formMethods.setValue('defecationDaysAgo', inputState.defecationDaysAgo)
    formMethods.setValue('defecationCondition', inputState.defecationCondition)
    formMethods.setValue('meal', inputState.meal)
    formMethods.setValue('mealTime', inputState.mealTime)
    formMethods.setValue('eatingMethod', inputState.eatingMethod)
    formMethods.setValue('breakfast', inputState.breakfast)
    formMethods.setValue('breakfastTime', inputState.breakfastTime)
    formMethods.setValue('breakfastWhat', inputState.breakfastWhat)
    formMethods.setValue('lunch', inputState.lunch)
    formMethods.setValue('lunchTime', inputState.lunchTime)
    formMethods.setValue('lunchWhat', inputState.lunchWhat)
    formMethods.setValue('snack', inputState.snack)
    formMethods.setValue('snackTime', inputState.snackTime)
    formMethods.setValue('eatingMethodDetail', inputState.eatingMethodDetail)
    formMethods.setValue('milkAmount', inputState.milkAmount)
    formMethods.setValue('milkTime', inputState.milkTime)
    formMethods.setValue('breastMilkTime', inputState.breastMilkTime)
    formMethods.setValue('escortType', inputState.escortType)
    formMethods.setValue('escort1Name', inputState.escort1Name)
    formMethods.setValue('escort1Relationship', inputState.escort1Relationship)
    formMethods.setValue('escort1Tel', inputState.escort1Tel ? inputState.escort1Tel : '')
    formMethods.setValue('escort2Name', inputState.escort2Name)
    formMethods.setValue('escort2Relationship', inputState.escort2Relationship)
    formMethods.setValue('escort2Tel', inputState.escort2Tel ? inputState.escort2Tel : '')
    formMethods.setValue('belongings', inputState.belongings ? inputState.belongings.split(',') : [])
    formMethods.setValue('belongingsMilkCount', inputState.belongingsMilkCount)
    formMethods.setValue('belongingsMilkDissolveHotWater', inputState.belongingsMilkDissolveHotWater)
    formMethods.setValue('belongingsOther', inputState.belongingsOther)
    formMethods.setValue('note', inputState.note)
  }
  return {
    history,
    reservationNo,
    todayConditionInputDetail,
    todayConditionInputDecodeMst,
    formMethods,
    isCough,
    isRunnyNose,
    isEczema,
    isAllergy,
    isFoodAllergy,
    isMedicine,
    isMood,
    isDisease,
    isVaccine,
    isHospitalVisit,
    isInfection,
    isTodayConditionPoint,
    isStroll,
    isLastNightSleep,
    isNap,
    napType,
    defecationTiming,
    isMeal,
    eatingMethod,
    breakfast,
    lunch,
    snack,
    eatingMethodDetail,
    escortType,
    belongings,
    onSubmit,
    isShowNoChange,
    setIsShowNoChange,
    yesNos,
  }
}

//------------------------------------------------------------------------------------------
